import type { CartQuoteLine } from "@pufimi/shared";
import type { TFunction } from "i18next";
import { PawPrint, Trash2 } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { formatPriceCents } from "@/features/orders/pricing";
import type { CartProjectGroup } from "../grouping";

// The rendered-pillow thumbnail for a cart line (ADR-0054 §2) — the product sub-item's
// image. A quiet placeholder holds the square while it loads / when a project has no
// render yet, so the tree never jumps. The signed URL is short-lived (300s) and points at
// storage we don't control at render time, so a load failure — an expired signature on a
// long-open page, a CDN blip — falls back to that same placeholder rather than leaving a
// broken image on a money surface. Reset per URL, so a re-signed URL gets a fresh try.
export function CartLineThumbnail({
  line,
  className = "size-14",
}: {
  line: CartQuoteLine;
  className?: string;
}) {
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const src = line.thumbnailUrl && line.thumbnailUrl !== failedUrl ? line.thumbnailUrl : null;
  return (
    <div className={`shrink-0 overflow-hidden rounded-lg bg-muted ${className}`}>
      {src ? (
        // biome-ignore lint/a11y/useAltText: decorative within a labeled row
        <img
          src={src}
          alt=""
          className="size-full object-contain"
          onError={() => setFailedUrl(src)}
        />
      ) : (
        <div className="flex size-full items-center justify-center">
          <PawPrint aria-hidden className="size-5 text-muted-foreground/50" />
        </div>
      )}
    </div>
  );
}

// The two-level tree's group head (ADR-0054 §2): one project heads a group of its
// Products. The flat pet photo isn't in the quote payload (assets stay project-level;
// surfacing it is a follow-up), so the head is a quiet labelled divider — enough to
// separate one pet's products from another's when the cart mixes pets.
export function CartProjectHeader({
  group,
  t,
}: {
  group: CartProjectGroup;
  t: TFunction<"orders">;
}) {
  return (
    <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
      <PawPrint aria-hidden className="size-3.5" />
      <span>{group.projectId ? t("cart.projectGroup") : t("cart.catalogGroup")}</span>
    </div>
  );
}

// Human product title for a cart line — the SERVER-computed, per-type name (`variantTitlePl`,
// ADR-0054 §3, #407). Rendered verbatim so a new product type (3D print, …) needs no
// client-side type switch here. Shared by the mini-cart and the checkout page.
export function cartLineTitle(line: CartQuoteLine): string {
  return line.title;
}

// One compact, read-only cart line for the mini-cart Sheet: thumbnail, product title,
// its footprint (the "variant"), the quantity, the per-line price, and a remove action.
// Editing quantities happens on the checkout page (the mini-cart stays a quick peek).
export function CartLineRow({
  line,
  currency,
  onRemove,
  t,
}: {
  line: CartQuoteLine;
  currency: string;
  onRemove: () => void;
  t: TFunction<"orders">;
}) {
  return (
    <div className="flex items-start gap-3" data-testid="cart-line">
      <CartLineThumbnail line={line} />
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <p className="truncate font-medium">{cartLineTitle(line)}</p>
        <p className="text-sm text-muted-foreground">
          {t("cart.dimensions", { width: line.widthCm, height: line.heightCm })}
        </p>
        <div className="mt-0.5 flex items-center gap-2 text-sm text-muted-foreground">
          <span data-testid="cart-line-qty">{t("cart.quantityShort", { n: line.quantity })}</span>
          <span aria-hidden>·</span>
          <span className="tabular-nums">{formatPriceCents(line.lineTotalCents, currency)}</span>
        </div>
      </div>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        aria-label={t("cart.remove")}
        onClick={onRemove}
        data-testid="cart-line-remove"
        className="size-8 shrink-0 text-muted-foreground hover:text-foreground"
      >
        <Trash2 aria-hidden className="size-4" />
      </Button>
    </div>
  );
}
